<template>
  <div v-if="visible" class="splash-screen">
    <!-- Animated Background Particles -->
    <div class="particles-container">
      <div
        v-for="i in 8"
        :key="i"
        class="particle"
        :style="{
          left: (i * 12) % 100 + '%',
          top: (i * 17) % 100 + '%',
          animationDelay: (i * 0.25) + 's',
          animationDuration: (3 + (i % 2)) + 's'
        }"
      />
    </div>

    <!-- Main Content -->
    <div class="splash-content">
      <!-- Animated Logo/Brand -->
      <div class="brand-container animate-fade-in-up">
        <div class="logo-icon animate-pulse-slow">
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="200px" height="200px">
  <defs>
    <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0" style="stop-color:#ffffff;stop-opacity:1"/>
      <stop offset="1" style="stop-color:#e8f5e9;stop-opacity:1"/>
    </linearGradient>
  </defs>
  <circle cx="75.063" cy="108.335" r="45" fill="none" stroke="url(#logoGradient)" stroke-width="3" class="draw-circle " style="stroke-width: 3;" transform="matrix(1.524298906326294, 0, 0, 1.4771239757537842, -25.743858337402344, -58.3493309020996)"/>
  <path d="M 47.512 75.718 C 57.954 65.277 75.658 65.277 86.099 75.718 C 96.54 65.277 114.251 65.277 124.693 75.718 C 135.134 86.159 132.308 100.392 124.926 110.803 C 115.682 123.841 86.099 145.843 86.099 145.843 C 86.099 145.843 86.099 145.843 86.099 145.843 C 86.099 145.843 86.099 145.843 86.099 145.843 C 86.099 145.843 86.099 145.843 86.099 145.843 C 65.747 129.423 52.963 117.744 47.746 110.803 C 39.921 100.392 37.071 86.159 47.512 75.718 Z" fill="url(#logoGradient)" class="heart-shape" style="stroke-width: 1;"/>
</svg>
        </div>

        <p class="brand-name animate-fade-in-up-delay" aria-label="{{ $t('loadingSplash.brand') }}">
          {{ $t('loadingSplash.brand') }}
        </p>
        <h3 class="brand-name brand-domain animate-fade-in-up-delay-1">sarvban.com</h3>
      </div>

      <!-- Animated Slogan -->
      <div class="slogan-container animate-fade-in-up-delay-2">
        <p class="slogan">
      {{sarvban_slogans[Math.floor(Math.random() * sarvban_slogans.length)]}}
        </p>
      </div>

      <!-- Animated Loading Indicator -->
      <div class="loading-container animate-fade-in-delay">
        <div class="loading-dots">
          <span
            v-for="i in 5"
            :key="i"
            class="dot"
            :style="{ animationDelay: (i * 0.15) + 's' }"
          />
        </div>
      </div>

      <!-- Decorative Elements -->
      <div class="decor-circle circle-1 animate-pulse-slow" />
      <div class="decor-circle circle-2 animate-pulse-slower" />
      <div class="decor-circle circle-3 animate-pulse-medium" />
    </div>

    <!-- Bottom Wave -->
    <div class="wave-container">
      <svg class="wave" viewBox="0 0 1440 120" preserveAspectRatio="none">
        <path
          d="M0,60 C360,120 720,0 1080,60 C1260,90 1380,40 1440,60 L1440,120 L0,120 Z"
          fill="rgba(255,255,255,0.1)"
          class="wave-path"
        />
      </svg>
    </div>
  </div>
</template>

<script setup>
const { t } = useI18n()

defineProps({
  visible: {
    type: Boolean,
    default: true
  }
})

const sarvban_slogans = [
  t('loadingSplash.slogan_1'),
  t('loadingSplash.slogan_2'),
  t('loadingSplash.slogan_3'),
  t('loadingSplash.slogan_4'),
  t('loadingSplash.slogan_5'),
  t('loadingSplash.slogan_6'),
  t('loadingSplash.slogan_7'),
  t('loadingSplash.slogan_8'),
  t('loadingSplash.slogan_9'),
  t('loadingSplash.slogan_10'),
  t('loadingSplash.slogan_11')
];
</script>

<style scoped>
    .heart-shape {
      transform-origin: center;
      filter: drop-shadow(0 0 20px rgba(255,255,255,0.4));
      animation:
        heartEnter 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards,
        heartBeat 1.2s ease-in-out 0.8s infinite;
    }

    @keyframes heartEnter {
      from {
        transform: translateY(-20px) scale(0);
        opacity: 0;
      }
      to {
        transform: translateY(0) scale(1);
        opacity: 1;
      }
    }

    @keyframes heartBeat {
      0%, 100% {
        transform: scale(1);
      }
      15% {
        transform: scale(1.25);
      }
      30% {
        transform: scale(1);
      }
      45% {
        transform: scale(1.15);
      }
      60% {
        transform: scale(1);
      }
    }

.draw-circle {
  stroke-dasharray: 283;
  stroke-dashoffset: 283;
  animation: draw 2s ease forwards;
}

.draw-path {
  transform-origin: center;
  transform: scale(0);
  animation: scale-in 0.8s ease 0.5s forwards;
}

.draw-center {
  transform-origin: center;
  transform: scale(0);
  animation: scale-in-bounce 0.6s ease 1s forwards;
}

@keyframes draw {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes scale-in {
  to {
    transform: scale(1);
  }
}

@keyframes scale-in-bounce {
  0% {
    transform: scale(0);
  }
  70% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

.splash-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #03a054 0%, #028a46 50%, #016d38 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  overflow: hidden;
}

.particles-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.particle {
  position: absolute;
  width: 6px;
  height: 6px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  filter: blur(1px);
  animation: float-particle 4s ease-in-out infinite;
}

@keyframes float-particle {
  0%, 100% {
    transform: translateY(0) scale(0);
    opacity: 0;
  }
  50% {
    transform: translateY(-30px) scale(1);
    opacity: 0.6;
  }
}
#logoGradient{
  filter: drop-shadow(0 4px 20px rgba(0, 0, 0, 0.3));
}
.splash-content {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.brand-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 30px;
}

.logo-icon {
  width: 120px;
  height: 120px;
  margin-bottom: 20px;
}

.logo-svg {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 4px 20px rgba(0, 0, 0, 0.3));
}



.brand-name {
  font-size: 3.5rem;
  font-weight: 900;
  color: white;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  margin: 0;
  letter-spacing: 2px;
  font-family: 'Vazirmatn', 'Tahoma', sans-serif;
}
.brand-domain{
font-size: 1.5rem;
}
.slogan-container {
  margin-bottom: 40px;
}

.slogan {
  font-size: 1.3rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  font-family: 'Vazirmatn', 'Tahoma', sans-serif;
}

.loading-container {
  margin-top: 20px;
}

.loading-dots {
  display: flex;
  gap: 12px;
}

.dot {
  width: 12px;
  height: 12px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  animation: bounce-dot 1s ease-in-out infinite;
}

@keyframes bounce-dot {
  0%, 100% {
    transform: translateY(-10px) scale(0.8);
    opacity: 0.4;
  }
  50% {
    transform: translateY(10px) scale(1.2);
    opacity: 1;
  }
}

.decor-circle {
  position: absolute;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.2);
  pointer-events: none;
}

.circle-1 {
  width: 300px;
  height: 300px;
  top: -100px;
  right: -100px;
}

.circle-2 {
  width: 250px;
  height: 250px;
  bottom: -80px;
  left: -80px;
}

.circle-3 {
  width: 200px;
  height: 200px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

@keyframes pulse-slow {
  0%, 100% {
    transform: scale(1);
    opacity: 0.2;
  }
  50% {
    transform: scale(1.5);
    opacity: 0.4;
  }
}

@keyframes pulse-slower {
  0%, 100% {
    transform: scale(1.2);
    opacity: 0.3;
  }
  50% {
    transform: scale(0.8);
    opacity: 0.1;
  }
}

@keyframes pulse-medium {
  0%, 100% {
    transform: translate(-50%, -50%) scale(0.9);
    opacity: 0.25;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.3);
    opacity: 0.15;
  }
}

.animate-pulse-slow {
  animation: pulse-slow 4s ease-in-out infinite;
}

.animate-pulse-slower {
  animation: pulse-slower 5s ease-in-out infinite;
}

.animate-pulse-medium {
  animation: pulse-medium 3.5s ease-in-out infinite;
}

.wave-container {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 120px;
  overflow: hidden;
}

.wave {
  width: 100%;
  height: 100%;
}

.wave-path {
  animation: wave-move 3s ease-in-out infinite;
}

@keyframes wave-move {
  0%, 100% {
    d: path("M0,60 C360,120 720,0 1080,60 C1260,90 1380,40 1440,60 L1440,120 L0,120 Z");
  }
  50% {
    d: path("M0,80 C360,40 720,100 1080,40 C1260,20 1380,80 1440,50 L1440,120 L0,120 Z");
  }
}

/* Fade in animations */
.animate-fade-in-up {
  animation: fadeInUp 0.8s ease forwards;
}

.animate-fade-in-up-delay {
  animation: fadeInUp 0.8s ease 0.3s forwards;
  opacity: 0;
}
.animate-fade-in-up-delay-1 {
  animation: fadeInUp 0.8s ease 0.5s forwards;
  opacity: 0;
}

.animate-fade-in-up-delay-2 {
  animation: fadeInUp 0.8s ease 0.6s forwards;
  opacity: 0;
}

.animate-fade-in-delay {
  animation: fadeIn 0.5s ease 1s forwards;
  opacity: 0;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .brand-name {
    font-size: 2.5rem;
  }

  .slogan {
    font-size: 1rem;
  }

  .logo-icon {
    width: 90px;
    height: 90px;
  }
}
</style>
